"use client";

import {
  BedDoubleIcon,
  CheckSquareIcon,
  DollarSignIcon,
  FilterIcon,
  ListChecks,
  Loader,
  RefreshCwIcon,
  SearchIcon,
  StarIcon,
  Trash2Icon,
  UtensilsIcon,
  XIcon,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import {
  clearAllHotelFilters,
  removeHotelFilterItem,
  useHotelFilter,
} from "@/feature/hotel/search/state/extract-hotel-filter-slice";

const HotelFilterPopover = () => {
  const dispatch = useAppDispatch();
  const { selectedOption, defaultOption } = useAppSelector(useHotelFilter);
  const loading = useAppSelector((state) => state.selectedHotelData.isLoading);
  const [isOpen, setIsOpen] = useState(false);

  const appliedFiltersCount = useMemo(() => {
    if (!selectedOption) return 0;
    let count = 0;

    if (selectedOption.hotelName) count++;

    if (
      selectedOption.priceRange &&
      defaultOption?.priceRange &&
      (selectedOption.priceRange.min !== defaultOption.priceRange.min ||
        selectedOption.priceRange.max !== defaultOption.priceRange.max)
    ) {
      count++;
    }

    if (selectedOption.ratings?.length) count += selectedOption.ratings.length;
    if (selectedOption.facilities?.length)
      count += selectedOption.facilities.length;
    if (selectedOption.mealPlans?.length)
      count += selectedOption.mealPlans.length;
    if (selectedOption.roomTypes?.length)
      count += selectedOption.roomTypes.length;
    if (selectedOption.cancellation?.length)
      count += selectedOption.cancellation.length;

    return count;
  }, [selectedOption, defaultOption]);

  const removeFilter = useCallback(
    (key: any, value?: any) => {
      dispatch(removeHotelFilterItem({ key, value }));
    },
    [dispatch],
  );

  const handleResetAll = useCallback(() => {
    dispatch(clearAllHotelFilters());
    setIsOpen(false);
  }, [dispatch]);

  const filterSections = useMemo(() => {
    type FilterItem = { label: string; onRemove: () => void };
    const sections: {
      title: string;
      icon: React.ReactNode;
      items: FilterItem[];
    }[] = [];
    if (!selectedOption) return sections;

    // Hotel Name
    if (selectedOption.hotelName) {
      sections.push({
        title: "Search",
        icon: <SearchIcon className="w-3.5 h-3.5" />,
        items: [
          {
            label: `Name: ${selectedOption.hotelName}`,
            onRemove: () => removeFilter("hotelName"),
          },
        ],
      });
    }

    // Price Range
    if (
      selectedOption.priceRange &&
      defaultOption?.priceRange &&
      (selectedOption.priceRange.min !== defaultOption.priceRange.min ||
        selectedOption.priceRange.max !== defaultOption.priceRange.max)
    ) {
      sections.push({
        title: "Price Range",
        icon: <DollarSignIcon className="w-3.5 h-3.5" />,
        items: [
          {
            label: `$${selectedOption.priceRange.min} - $${selectedOption.priceRange.max}`,
            onRemove: () => removeFilter("priceRange"),
          },
        ],
      });
    }

    // Ratings
    if (selectedOption.ratings?.length) {
      sections.push({
        title: "Star Ratings",
        icon: <StarIcon className="w-3.5 h-3.5" />,
        items: selectedOption.ratings.map((rating: any) => ({
          label: rating === 0 ? "Default" : `${rating} Stars`,
          onRemove: () => removeFilter("ratings", rating),
        })),
      });
    }

    // Facilities
    if (selectedOption.facilities?.length) {
      sections.push({
        title: "Facilities",
        icon: <CheckSquareIcon className="w-3.5 h-3.5" />,
        items: selectedOption.facilities.map((fac: any) => ({
          label: fac,
          onRemove: () => removeFilter("facilities", fac),
        })),
      });
    }

    // Meal Plans
    if (selectedOption.mealPlans?.length) {
      sections.push({
        title: "Meal Plans",
        icon: <UtensilsIcon className="w-3.5 h-3.5" />,
        items: selectedOption.mealPlans.map((meal: any) => ({
          label: meal,
          onRemove: () => removeFilter("mealPlans", meal),
        })),
      });
    }

    // Room Types
    if (selectedOption.roomTypes?.length) {
      sections.push({
        title: "Room Types",
        icon: <BedDoubleIcon className="w-3.5 h-3.5" />,
        items: selectedOption.roomTypes.map((room: any) => ({
          label: room,
          onRemove: () => removeFilter("roomTypes", room),
        })),
      });
    }

    // Cancellation
    if (selectedOption.cancellation?.length) {
      sections.push({
        title: "Cancellation Policy",
        icon: <RefreshCwIcon className="w-3.5 h-3.5" />,
        items: selectedOption.cancellation.map((policy: any) => ({
          label: policy,
          onRemove: () => removeFilter("cancellation", policy),
        })),
      });
    }

    return sections;
  }, [selectedOption, defaultOption, removeFilter]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild disabled={appliedFiltersCount === 0 && !loading}>
        <Button variant="ghost" size="sm" className="mr-2">
          <ListChecks className="w-3.5 h-3.5" />
          <Badge
            className="h-4 w-4 p-0 text-[10px] flex items-center justify-center"
            variant="destructive"
          >
            {loading ? (
              <Loader className="size-1 animate-spin" />
            ) : (
              appliedFiltersCount
            )}
          </Badge>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        hidden={appliedFiltersCount === 0}
        className="w-72 p-0 bg-background border-border shadow-lg"
        align="end"
        sideOffset={4}
      >
        <div className="p-3 border-b border-border">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <FilterIcon className="w-4 h-4 text-primary" />
              <h3 className="font-semibold text-sm">Applied Filters</h3>
            </div>
            <div className="flex items-center gap-1">
              {appliedFiltersCount > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResetAll}
                  className="h-7 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2Icon className="w-3 h-3" />
                  Clear All
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="h-7 w-7 p-0"
              >
                <XIcon className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {appliedFiltersCount} filter{appliedFiltersCount !== 1 ? "s" : ""}{" "}
            applied
          </p>
        </div>

        {appliedFiltersCount === 0 ? (
          <div className="text-center py-6 px-4">
            <div className="bg-muted w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
              <FilterIcon className="w-5 h-5 text-muted-foreground" />
            </div>
            <p className="font-medium text-sm text-foreground">
              No filters applied
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Apply filters to narrow down your search
            </p>
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto bg-muted">
            <div className="space-y-3">
              {filterSections.map((section, sectionIndex) => (
                <div
                  key={sectionIndex}
                  className="space-y-2 m-1.5 p-1.5 bg-background rounded-md border"
                >
                  <div className="flex items-center gap-2 px-1">
                    <span className="text-muted-foreground">
                      {section.icon}
                    </span>
                    <h4 className="text-xs font-medium text-foreground">
                      {section.title}
                    </h4>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {section.items.map((item, itemIndex) => (
                      <Badge
                        key={itemIndex}
                        variant="secondary"
                        className="text-xs border-border transition-colors flex items-center max-w-full px-1"
                      >
                        <span className="truncate max-w-48 text-xs">
                          {item.label}
                        </span>
                        <button
                          onClick={item.onRemove}
                          className="rounded-full hover:bg-muted transition-colors shrink-0 cursor-pointer"
                        >
                          <XIcon className="w-2.5 h-2.5" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default HotelFilterPopover;
